import React from 'react';
import {
  Button, Card, Grid, Typography,
} from '@mui/material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import {
  UserMissionCategoryPopulated, UserMissionsStatuses,
} from '@gamiphy/bl/mission';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { DisplayStringTrans, useTranslation } from '@gamiphy/react-i18n';
import { useMissionHandler } from '@gamiphy/loyalty-station-desktop/modules/missions';
import { playButtonSound } from '@gamiphy/loyalty-station-desktop/sounds';
import { UnavowedPopup } from '@gamiphy/loyalty-station-desktop/components/popups/unavowed';
import SVG from 'react-inlinesvg';
import { Points } from '@gamiphy/loyalty/points';
import { observer } from 'mobx-react';
import { MissionTitle } from '@gamiphy/loyalty/missions';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';

export type Props = {
  userMission: UserMissionCategoryPopulated,
}

const MissionContent: React.FC<Props> = observer(({ userMission }) => {
  const [popupOpen, setPopupOpen] = React.useState(false);

  const { t } = useTranslation();

  const missionHandler = useMissionHandler(userMission);

  const onMissionCtaClick = React.useCallback(() => {
    if (UserStore.isRealUser) {
      void playButtonSound();
      if (userMission.mission.cta?.action) {
        missionHandler?.action(userMission);
      }
    } else {
      setPopupOpen(true);
    }
  }, [UserStore.isRealUser, userMission.mission.cta?.action, missionHandler]);

  return (
    <Card sx={(theme) => ({
      m: 0,
      width: '100%',
      boxShadow: 'none',
      borderRadius: 0,
      pb: theme.spacing(2),
    })}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        columnSpacing={1}
      >
        <Grid>
          <SVG src={userMission.mission.image} style={{ width: '2.4rem', height: '2.4rem' }} />
        </Grid>
        <Grid sx={{ flex: 2 }} container direction="column">
          <Grid size={12}>
            <Typography
              variant="subtitle1"
              sx={(theme) => ({
                fontSize: '1.2rem',
                fontWeight: 500,
                lineHeight: 1.25,
                mb: theme.spacing(0.4),
              })}
            >
              <MissionTitle mission={userMission.mission} category={userMission.mission.category} />
            </Typography>
          </Grid>
          <Grid size={12} container>
            <Points points={userMission.mission.points} tierConfig={LevelStore.tierConfig} />
          </Grid>
        </Grid>
        {
          userMission.status === UserMissionsStatuses.completed
            ? (
              <CheckCircleOutlineOutlinedIcon color="success" fontSize="large" />
            )
            : (
              userMission.mission.cta && !missionHandler?.hideButton && (
                <Grid>
                  <Button
                    sx={{ width: '8rem', fontSize: '1rem' }}
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={onMissionCtaClick}
                  >
                    <DisplayStringTrans displayString={userMission.mission.cta.label} />
                  </Button>
                  <UnavowedPopup
                    open={popupOpen}
                    onClose={() => setPopupOpen(false)}
                    title={t('unavowedPopup.rewardTitle', 'Sign up now to redeem your coupon for exclusive discounts!')}
                  />
                </Grid>
              )
            )
        }
      </Grid>
    </Card>
  );
});

export default MissionContent;
